import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface MediaConfiguration {
  cameraDevice: string;
}

@Injectable({
  providedIn: 'root',
})
export class MediaInputService {
  videoDevices: MediaDeviceInfo[] | undefined;
  
  private _selectedVideoDevice: string | undefined;

  get selectedVideoDevice(): string | undefined {
    return this._selectedVideoDevice;
  }

  set selectedVideoDevice(value: string | undefined) {
    this._selectedVideoDevice = value;
    if (value) {
      localStorage.setItem('selectedVideoDevice', value);
    }
  }

  private readonly mediaConfiguration =
    new BehaviorSubject<MediaConfiguration | null>(null);
  mediaConfiguration$ = this.mediaConfiguration.asObservable();

  setMediaDeviceInfo(mediaDeviceInfo: MediaDeviceInfo[]) {
    this.videoDevices = mediaDeviceInfo.filter(
      (info: MediaDeviceInfo) => info.kind === 'videoinput',
    );

    if (this.videoDevices.length > 0) {
      const storedDevice = localStorage.getItem('selectedVideoDevice');
      const deviceExists = this.videoDevices.find(d => d.deviceId === storedDevice);
      if (deviceExists) {
        this.selectedVideoDevice = deviceExists.deviceId;
      } else {
        this.selectedVideoDevice = this.videoDevices[0].deviceId;
      }
    }
  }

  getCurrentMediaConfiguration(): MediaConfiguration {
    const currentConfiguration = this.mediaConfiguration.value;
    if (currentConfiguration == null) {
      throw new Error(
        'try to access current media configuration while it is not available',
      );
    }
    return currentConfiguration;
  }

  setCurrentMediaConfiguration(cameraDevice: string) {
    this.mediaConfiguration.next({
      cameraDevice: cameraDevice,
    });
  }

  async getVideoStream(): Promise<MediaStream> {
    return navigator.mediaDevices.getUserMedia({
      video: { deviceId: this.selectedVideoDevice! },
    });
  }

  /**
   * Closes all tracks of the given MediaStream, effectively stopping media capture
   * @param stream
   */
  closeMediaStreamTracks(stream: MediaStream): void {
    for (const track of stream.getTracks()) {
      track.stop();
    }
  }

  resetMediaConfiguration() {
    this.mediaConfiguration.next(null);
  }
}
